import { Link } from '@tanstack/react-router';
import { MonoLabel, StripePlaceholder } from '@/component';
import type { PackCard } from '@/hook';

const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;

/** "Added June" from the publish date; a pack in Free comes with the account. */
export function packAddedLabel(pack: Pick<PackCard, 'includedInPlans' | 'publishedAt'>): string {
  if (pack.includedInPlans.includes('free')) return 'Free with every account';
  if (pack.publishedAt === null) return '';
  return `Added ${new Date(pack.publishedAt).toLocaleDateString('en-US', { month: 'long' })}`;
}

/** A pack the caller can play: 16:9 art, song count, name, when it arrived. */
export function LibraryPackCard({ pack }: { pack: PackCard }) {
  return (
    <Link
      to="/library/packs/$packId"
      params={{ packId: pack._id }}
      className="overflow-hidden rounded-lg border border-line bg-surface text-ink hover:border-line2"
    >
      {pack.coverUrl ? (
        <img src={pack.coverUrl} alt="" className="aspect-video w-full object-cover" />
      ) : (
        <StripePlaceholder caption="pack art" className="aspect-video" />
      )}
      <div className="px-5 pb-5 pt-[18px]">
        <MonoLabel size="md" className="block">
          {plural(pack.songCount, 'song')}
        </MonoLabel>
        <div className="mt-[9px] text-[17px] font-semibold tracking-[-0.02em]">{pack.name}</div>
        <div className="mt-1 text-[13px] text-mute">{packAddedLabel(pack)}</div>
      </div>
    </Link>
  );
}

/** A pack the caller cannot play yet: quieter, dashed, no art. */
export function LibraryLockedPackCard({ pack, note }: { pack: PackCard; note: string }) {
  return (
    <Link
      to="/library/packs/$packId"
      params={{ packId: pack._id }}
      className="rounded-lg border border-dashed border-line2 p-5 text-ink hover:border-line"
    >
      <MonoLabel size="md" className="block">
        {plural(pack.songCount, 'song')}
      </MonoLabel>
      <div className="mt-[9px] text-[17px] font-semibold tracking-[-0.02em] text-mute">{pack.name}</div>
      <div className="mt-1 text-[13px] text-faint">{note}</div>
    </Link>
  );
}
